import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';

import { currentArchiveIdAtom } from '../../atoms/context';
import { useMoveIntoFolderLogic } from './move-into-folder/useMoveIntoFolderLogic';
import FolderListVirtualized from './move-into-folder/FolderListVirtualized';

export interface MoveIntoFolderModalProps {
  fileId: string;
  archiveId?: string;
  currentFolderId?: string;
  onSubmit: (destFolderId: string) => void | Promise<void>;
}

const MoveIntoFolderModal: React.FC<MoveIntoFolderModalProps> = ({
  fileId: _fileId,
  archiveId,
  currentFolderId,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [currentArchiveId] = useAtom(currentArchiveIdAtom);

  const effectiveArchiveId = archiveId || currentArchiveId || '';
  const { loading, search, setSearch, selectedId, setSelectedId, filtered } =
    useMoveIntoFolderLogic({ archiveId: effectiveArchiveId, currentFolderId });

  const handleConfirm = React.useCallback(async () => {
    if (!selectedId) return;
    await onSubmit(selectedId);
    closeModal();
  }, [onSubmit, selectedId]);

  return (
    <ModalBase className="min-w-[420px]">
      <X
        className="absolute right-3 top-3 cursor-pointer text-slate-500 hover:text-slate-700"
        onClick={closeModal}
        aria-label="Close"
      />
      <h3 className="mb-4 pr-6 text-md">{t('move-to-folder')}</h3>

      <div className="space-y-3">
        <Input
          label={t('search')}
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="max-h-72">
          {loading ? (
            <div className="p-4 text-center text-sm text-slate-500">
              <Spinner className="size-10 [&_*]:stroke-primary-dark" />
            </div>
          ) : !effectiveArchiveId ? (
            <div className="p-4 text-center text-sm text-slate-500">
              {t('no-archive-selected') || 'No archive context to list folders.'}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              {t('no-results') || 'No folders found.'}
            </div>
          ) : (
            <FolderListVirtualized
              items={filtered}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <Button onClick={closeModal} variant="outline" className="border-red-500 text-red-500">
            {t('cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!selectedId}>
            {t('move') || 'Move'}
          </Button>
        </div>
      </div>
    </ModalBase>
  );
};

export default MoveIntoFolderModal;
